import React, { FunctionComponent, useCallback, useMemo } from 'react';
import moment from 'moment';
import Image from '@components/Image';
import { Link } from 'react-router-dom';
import { getPath } from '@helpers/navigation';
import { LOCALE_NAME } from '@stores/localeWeight';
import { FEATURING_TYPE } from '@pages/FeaturingPage/FeaturingEditModal/FeaturingEditModal.types';
import { Tooltip, Tag } from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  StarOutlined,
  StopOutlined,
  EditOutlined,
  HeartOutlined,
  CommentOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { FULL_TIME_FORMAT } from '@config/format';
import cn from 'classnames';
import { openModal } from '@stores/modals';
import featuringStore, { block, unblock } from '@stores/featuring';
import { useStore } from 'effector-react';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../FeaturingPage.module.json';
import { IFeaturing } from '@interfaces/IFeaturing';
import useGuards from '@hooks/useGuards';
import css from './FeaturingPost.module.less';

interface FeaturingPostProps {
  featuring: IFeaturing;
}

function getListTag<T extends { [key: string]: string | number | any }>(
  list: T[],
  idKey: string,
  nameKey: string,
  color: string,
  size = 2,
): JSX.Element[] {
  const listTag = list.slice(0, size).map((item) => (
    <Tag
      color={color}
      key={item[idKey]}
    >
      {item[nameKey]}
    </Tag>
  ));
  if (list.length > size) {
    const gameOver = list
      .slice(size)
      .map((item) => item[nameKey])
      .join(', ');
    listTag.push(
      <Tooltip
        placement='right'
        trigger='hover'
        title={gameOver}
        key='over'
      >
        <Tag color={color}>...</Tag>
      </Tooltip>,
    );
  }
  return listTag;
}

const FeaturingPost: FunctionComponent<FeaturingPostProps> = ({ featuring }) => {
  const content = useTranslate(componentContent);
  const { preloaders } = useStore(featuringStore);
  const { allowChange } = useGuards();

  const isBlocking = useMemo(() => {
    return [preloaders.block, preloaders.unblock].includes(`block_${featuring.id.toString()}`);
  }, [featuring.id, preloaders.block, preloaders.unblock]);

  const games = useMemo(() => {
    const color = 'orange';
    if (featuring.games.length === 0) {
      return [
        <Tag
          color={color}
          key='over'
        >
          {content.all}
        </Tag>,
      ];
    }
    return getListTag(featuring.games, 'id', 'name', color);
  }, [content.all, featuring.games]);

  const platforms = useMemo(() => {
    const color = 'blue';
    if (featuring.platforms.length === 0) {
      return [
        <Tag
          color={color}
          key='over'
        >
          {content.all}
        </Tag>,
      ];
    }
    return getListTag(featuring.platforms, 'id', 'description', color);
  }, [content.all, featuring.platforms]);

  const blockFeaturing = useCallback(
    () =>
      !isBlocking
        ? block({
            featuringId: featuring.id.toString(),
            preloaderId: `block_${featuring.id.toString()}`,
          })
        : {},
    [featuring.id, isBlocking],
  );

  const unblockFeaturing = useCallback(
    () =>
      !isBlocking
        ? unblock({
            featuringId: featuring.id.toString(),
            preloaderId: `block_${featuring.id.toString()}`,
          })
        : {},
    [featuring.id, isBlocking],
  );

  const editFeaturing = useCallback(() => {
    openModal({ name: 'featuring', data: featuring });
  }, [featuring]);

  const timeBlock = useMemo(() => {
    return (
      <div className={css.metaLineItem}>
        <span className={css.metaIcon}>
          <ClockCircleOutlined />
        </span>
        <span>
          {`${moment(featuring.startDate).format(FULL_TIME_FORMAT)} - ${
            featuring.endDate ? moment(featuring.endDate).format(FULL_TIME_FORMAT) : ''
          }`}
        </span>
      </div>
    );
  }, [featuring]);

  const tags = ['ru', 'en', 'cn'];
  return (
    <div className={cn(css.root, !featuring.active ? css.notActive : '')}>
      <div className={css.image}>
        <Image
          className={css.image}
          url={featuring.post.image?.url || ''}
        />
      </div>
      <div className={css.content}>
        <div className={css.header}>
          <div>
            <Link
              to={getPath('postDetail', { postId: featuring.post.id })}
              className='clickable'
            >
              {content.post_id}: {featuring.post.id}
            </Link>
          </div>
        </div>
        <div className={css.body}>
          <div className={css.info}>
            <span className={css.title}>{content.locales}:</span>
            <Tag color='success'>{!featuring.locale ? content.all : tags}</Tag>
          </div>
          <div className={css.info}>
            <span className={css.title}>{content.games}: </span>
            {games}
          </div>
          <div className={css.info}>
            <span className={css.title}>{content.platforms}: </span>
            {platforms}
          </div>
          <div className={cn(css.metaLine, css.meta)}>
            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <HeartOutlined />
              </span>
              <span>{featuring.post.likesCount}</span>
            </div>
            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <CommentOutlined />
              </span>
              <span>{featuring.post.commentsCount}</span>
            </div>
            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <EyeOutlined />
              </span>
              <span>{featuring.post.viewsCount}</span>
            </div>
          </div>
        </div>
        <div className={css.footer}>
          <div className={cn(css.metaLine, css.meta)}>
            <div className={css.metaLineItem}>
              <span className={css.metaIcon}>
                <StarOutlined />
              </span>
              <span>{featuring.type === FEATURING_TYPE.STAGE_1 ? 1 : 2}</span>
            </div>
            {timeBlock}
            {allowChange ? (
              <div className={css.metaLineItem}>
                <span className={css.metaIcon}>
                  {isBlocking ? (
                    <LoadingOutlined />
                  ) : featuring.blocked ? (
                    <CheckCircleOutlined />
                  ) : (
                    <StopOutlined />
                  )}
                </span>
                {featuring.blocked ? (
                  <span
                    className='clickable'
                    onClick={unblockFeaturing}
                  >
                    {content.unblock}
                  </span>
                ) : (
                  <span
                    className='clickable'
                    onClick={blockFeaturing}
                  >
                    {content.block}
                  </span>
                )}
              </div>
            ) : null}
            {allowChange ? (
              <div className={css.metaLineItem}>
                <span className={css.metaIcon}>
                  <EditOutlined />
                </span>
                <span
                  className='clickable'
                  onClick={editFeaturing}
                >
                  {content.editing}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturingPost;
